/** Where an emailed password link lands: choose a password, then sign straight in with it. */

import { useState, type FormEvent } from "react";
import { Navigate } from "react-router";

import { useAuth } from "@/auth/AuthProvider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import { PasswordFields, passwordProblem } from "./PasswordFields";

export function ChoosePasswordPage() {
  const { state, finishPasswordLink } = useAuth();
  const code = new URLSearchParams(window.location.search).get("oobCode");
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.status === "signed-in") return <Navigate to="/" replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const problem = passwordProblem(password, again);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      await finishPasswordLink(code ?? "", password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "the password could not be set");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-4 px-4">
      {!code ? (
        <div className="space-y-3">
          <p>This password link is incomplete. Ask for a new one from the sign-in page.</p>
          <Button onClick={() => window.location.assign("/login")}>Go to sign-in</Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <Label className="text-base">Choose a password</Label>
          <p className="text-sm text-muted-foreground">
            You can still sign in with an email link or Google as well.
          </p>
          <PasswordFields password={password} again={again} onPassword={setPassword} onAgain={setAgain} />
          <Button type="submit" className="w-full" disabled={busy || state.status === "loading"}>
            Save and sign in
          </Button>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </form>
      )}
    </main>
  );
}
